import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface SectionTab {
  key: string;
  label: string;
  /** Results typed so far, out of the tests in this section. */
  done: number;
  total: number;
  /** Values outside the reference range. */
  flagged: number;
}

/**
 * One tab per test in a report, in a row that scrolls sideways when there are many. The active
 * tab scrolls into view, arrows appear at an edge that has more, and Left, Right, Home and End
 * move between tabs.
 */
export function SectionTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: SectionTab[];
  active: number;
  onChange: (i: number) => void;
}) {
  const strip = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = strip.current;
    if (!el) return;
    setEdge({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: tabs change what fits
  useEffect(() => {
    measure();
    const el = strip.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, tabs.length]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll the newly selected tab into view
  useEffect(() => {
    strip.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [active]);

  const nudge = (dir: -1 | 1) =>
    strip.current?.scrollBy({ left: dir * strip.current.clientWidth * 0.7, behavior: 'smooth' });

  function onKey(e: React.KeyboardEvent) {
    const last = tabs.length - 1;
    const next =
      e.key === 'ArrowRight'
        ? Math.min(last, active + 1)
        : e.key === 'ArrowLeft'
          ? Math.max(0, active - 1)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    if (next === null) return;
    e.preventDefault();
    onChange(next);
    strip.current?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  }

  return (
    <div className="relative border-b border-line">
      {edge.left ? (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-canvas to-transparent" />
          <button
            type="button"
            aria-label="Scroll the tests left"
            onClick={() => nudge(-1)}
            className="absolute top-1/2 left-0 z-20 -translate-y-1/2 rounded-full border border-line bg-paper p-1 text-slate shadow-sm hover:text-ink"
          >
            <ChevronLeft size={14} />
          </button>
        </>
      ) : null}
      {edge.right ? (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-canvas to-transparent" />
          <button
            type="button"
            aria-label="Scroll the tests right"
            onClick={() => nudge(1)}
            className="absolute top-1/2 right-0 z-20 -translate-y-1/2 rounded-full border border-line bg-paper p-1 text-slate shadow-sm hover:text-ink"
          >
            <ChevronRight size={14} />
          </button>
        </>
      ) : null}
      <div
        ref={strip}
        role="tablist"
        aria-label="Tests in this report"
        tabIndex={-1}
        onScroll={measure}
        onKeyDown={onKey}
        className="flex snap-x gap-1 overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((t, i) => {
          const on = i === active;
          const complete = t.total > 0 && t.done >= t.total;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              tabIndex={on ? 0 : -1}
              onClick={() => onChange(i)}
              className={clsx(
                'relative flex shrink-0 snap-start items-center gap-2 whitespace-nowrap px-3.5 py-2.5 text-[13px] transition-colors',
                on ? 'font-semibold text-navy' : 'text-slate hover:text-ink',
              )}
            >
              <span className="max-w-56 truncate">{t.label}</span>
              {t.total > 0 ? (
                <span
                  className={clsx(
                    'tabular rounded-full px-1.5 py-0.5 text-[11px] font-medium',
                    complete ? 'bg-emerald-50 text-ok' : 'bg-canvas text-slate',
                  )}
                  title={`${t.done} of ${t.total} results entered`}
                >
                  {t.done}/{t.total}
                </span>
              ) : null}
              {t.flagged > 0 ? (
                <span
                  className="rounded-full bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-flag"
                  title={`${t.flagged} outside the reference range`}
                >
                  {t.flagged} flagged
                </span>
              ) : null}
              <span
                className={clsx(
                  'absolute inset-x-2 bottom-0 h-0.5 rounded-full',
                  on ? 'bg-accent' : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
